import { Card } from "@/components/ui/card";
import { CheckCircle2, HeartPulse } from "lucide-react";
import type { NHSHypertensionInitialFormData } from "./NHSHypertensionInitialForm";

interface Props {
  isDarkMode?: boolean;
  data: NHSHypertensionInitialFormData;
}

const methodOfEntryMap: Record<string, string> = {
  "pharmacy-identified": "Patient identified in the pharmacy",
  "self-referral": "Patient self referral",
  "gp-practice": "Referred by GP practice",
  "covid-vaccination-centre": "Referred by COVID vaccination centre",
  optician: "Referred by optician",
  dentist: "Referred by dentist",
};

const outcomeMap: Record<string, string> = {
  "offer-abpm": "Offer ABPM (proceed to Stage 2)",
  "no-action-normal": "No action – BP within normal range",
  "refer-gp": "Referred to GP (non-urgent)",
  "refer-urgent": "Urgent same-day referral",
};

const formatDate = (s: string) =>
  s
    ? new Date(s).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "Not specified";

const avg = (a: string, b: string) => {
  const na = Number(a);
  const nb = Number(b);
  if (!a || !b || Number.isNaN(na) || Number.isNaN(nb)) return null;
  return Math.round((na + nb) / 2);
};

export function NHSHypertensionInitialSummary({
  isDarkMode = false,
  data,
}: Props) {
  const sysAvg = avg(data.bp2Systolic, data.bp3Systolic);
  const diaAvg = avg(data.bp2Diastolic, data.bp3Diastolic);

  const cardCls = `p-4 rounded-2xl border ${
    isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
  }`;
  const titleCls = `text-sm font-semibold ${
    isDarkMode ? "text-white" : "text-gray-900"
  }`;
  const labelCls = `text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`;
  const valueCls = `text-sm ${isDarkMode ? "text-gray-100" : "text-gray-900"}`;

  return (
    <div className="space-y-4">
      <div
        className={`flex items-start gap-3 p-4 rounded-2xl ${
          isDarkMode
            ? "bg-emerald-900/20 border border-emerald-800"
            : "bg-emerald-50 border border-emerald-200"
        }`}
      >
        <CheckCircle2
          className={`w-5 h-5 mt-0.5 ${
            isDarkMode ? "text-emerald-300" : "text-emerald-600"
          }`}
        />
        <div>
          <div
            className={`text-sm font-semibold ${
              isDarkMode ? "text-emerald-200" : "text-emerald-900"
            }`}
          >
            Stage 1 – Initial Consultation Completed
          </div>
          <div className={labelCls}>
            {formatDate(data.consultationDate)} · {data.consultationTime || "—"}{" "}
            · {data.consultationMethod || "—"}
          </div>
        </div>
      </div>

      <Card className={cardCls}>
        <div className="flex items-center gap-2 mb-3">
          <HeartPulse
            className={`w-4 h-4 ${
              isDarkMode ? "text-rose-300" : "text-rose-600"
            }`}
          />
          <h4 className={titleCls}>Clinic Blood Pressure</h4>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => {
            const sys = (data as any)[`bp${i}Systolic`];
            const dia = (data as any)[`bp${i}Diastolic`];
            const arm = (data as any)[`bp${i}Arm`];
            return (
              <div key={i}>
                <div className={labelCls}>
                  Reading {i}
                  {i === 1 ? " (discarded)" : ""}
                </div>
                <div className={valueCls}>
                  {sys && dia ? `${sys}/${dia} mmHg` : "—"}
                </div>
                <div className={`${labelCls} mt-1`}>Arm: {arm || "—"}</div>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-dashed">
          <div>
            <div className={labelCls}>Average (R2 &amp; R3)</div>
            <div className={`${valueCls} font-semibold`}>
              {sysAvg && diaAvg ? `${sysAvg}/${diaAvg} mmHg` : "—"}
            </div>
          </div>
          <div>
            <div className={labelCls}>Pulse</div>
            <div className={valueCls}>
              {data.pulseRate ? `${data.pulseRate} bpm` : "—"}
            </div>
          </div>
          <div>
            <div className={labelCls}>Pulse rhythm</div>
            <div className={valueCls}>
              {data.irregularPulse ? "Irregular detected" : "Regular"}
            </div>
          </div>
        </div>
      </Card>

      <Card className={cardCls}>
        <h4 className={titleCls}>Eligibility</h4>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div>
            <div className={labelCls}>Aged 40+</div>
            <div className={valueCls}>{data.ageEligible || "—"}</div>
          </div>
          <div>
            <div className={labelCls}>Already diagnosed</div>
            <div className={valueCls}>{data.alreadyDiagnosed || "—"}</div>
          </div>
          <div>
            <div className={labelCls}>Pregnant</div>
            <div className={valueCls}>{data.pregnant || "—"}</div>
          </div>
        </div>
        {data.methodOfEntry && (
          <div className="mt-3">
            <div className={labelCls}>How patient was identified</div>
            <div className={valueCls}>
              {methodOfEntryMap[data.methodOfEntry] || data.methodOfEntry}
            </div>
          </div>
        )}
        {data.methodOfEntry &&
          data.methodOfEntry !== "pharmacy-identified" &&
          data.methodOfEntry !== "self-referral" && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <div className={labelCls}>Referrer case reference</div>
                <div className={valueCls}>
                  {data.referrerCaseReference || "—"}
                </div>
              </div>
              <div>
                <div className={labelCls}>Referred for</div>
                <div className={valueCls}>
                  {data.referredFor === "clinic-bp-check"
                    ? "Clinic BP check"
                    : data.referredFor === "abpm"
                      ? "ABPM"
                      : "—"}
                </div>
              </div>
            </div>
          )}
      </Card>

      <Card className={cardCls}>
        <h4 className={titleCls}>ABPM Consent</h4>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <div className={labelCls}>Patient consents to ABPM</div>
            <div className={valueCls}>{data.abpmConsent || "—"}</div>
          </div>
          {data.abpmConsent === "yes" && (
            <div>
              <div className={labelCls}>ABPM available to fit now</div>
              <div className={valueCls}>{data.abpmAvailableNow || "—"}</div>
            </div>
          )}
        </div>
      </Card>

      {((data as any).healthyLivingAdvice?.length > 0 ||
        (data as any).healthyLivingAdviceNotes) && (
        <Card className={cardCls}>
          <h4 className={titleCls}>Healthy Living Advice Given</h4>
          {(data as any).healthyLivingAdvice?.length > 0 && (
            <ul className={`${valueCls} mt-2 list-disc pl-5 space-y-1`}>
              {(data as any).healthyLivingAdvice.map((v: string) => {
                const map: Record<string, string> = {
                  "diet-nutrition": "Diet & nutrition",
                  "sodium-salt": "Sodium / salt",
                  caffeine: "Caffeine",
                  alcohol: "Alcohol",
                  smoking: "Smoking",
                  "physical-activity": "Physical activity",
                  "referral-local-healthy-living":
                    "Referral to a local Healthy Living service",
                };
                return <li key={v}>{map[v] || v}</li>;
              })}
            </ul>
          )}
          {(data as any).healthyLivingAdviceNotes && (
            <div className="mt-3">
              <div className={labelCls}>Notes</div>
              <div className={`${valueCls} whitespace-pre-wrap`}>
                {(data as any).healthyLivingAdviceNotes}
              </div>
            </div>
          )}
        </Card>
      )}

      <Card className={cardCls}>
        <h4 className={titleCls}>Outcome</h4>
        <div className="mt-3 space-y-2">
          <div>
            <div className={labelCls}>Decision</div>
            <div className={valueCls}>
              {outcomeMap[data.outcome] || data.outcome || "—"}
            </div>
          </div>
          {data.outcomeNotes && (
            <div>
              <div className={labelCls}>Notes</div>
              <div className={valueCls}>{data.outcomeNotes}</div>
            </div>
          )}
        </div>
      </Card>

      {data.outcome === "offer-abpm" && (
        <Card className={cardCls}>
          <h4 className={titleCls}>Planned Next Stages</h4>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <div className={labelCls}>ABPM fitting (Stage 2)</div>
              <div className={valueCls}>{formatDate(data.abpmFittingDate)}</div>
            </div>
            <div>
              <div className={labelCls}>ABPM follow-up (Stage 3)</div>
              <div className={valueCls}>
                {formatDate(data.abpmFollowUpDate)}
              </div>
            </div>
          </div>
        </Card>
      )}

      {data.internalNotes && (
        <Card className={cardCls}>
          <h4 className={titleCls}>Internal Notes</h4>
          <div className={`${valueCls} mt-2 whitespace-pre-wrap`}>
            {data.internalNotes}
          </div>
        </Card>
      )}
    </div>
  );
}
